import {useEffect, useMemo, useState} from 'react';
import {useLazyQuery, useQuery} from '@apollo/client';
import {Avatar, Form, Input, Select, Table, Tag, Tooltip} from 'antd';
import {UserOutlined} from '@ant-design/icons';
import {toast} from 'react-toastify';
import {useSelector} from 'react-redux';
import DBModal from 'components/modal/modal';
import SubmitButton from 'components/Startup/components/SubmitButton';
import moment from 'moment';
import {GET_INVESTMENT_PURPOSE_USERS} from '../resolvers';
import styles from '../analytics.module.scss';
import UserLink from 'components/userLink/UserLink';
import {formatEnumLabel} from './enumFormat';
import {isApprovedKycStatus} from 'utils/kycStatus';

/**
 * API returns a map like { INVEST_IN_STOCKS_BONDS: true, OTHER: false } or legacy array/string.
 * @returns {string[]} active purpose keys (enum strings), excluding GraphQL metadata keys.
 */
const getActiveInvestmentPurposeKeys = raw => {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw.filter(v => v != null && v !== '').map(String);
  }
  if (typeof raw === 'object') {
    return Object.entries(raw)
      .filter(
        ([key, val]) =>
          typeof key === 'string' && !key.startsWith('__') && val === true,
      )
      .map(([key]) => key);
  }
  return [String(raw)];
};

const kycStatusDisplay = raw => {
  if (raw == null || raw === '') {
    return {label: '—', color: undefined};
  }
  const code = String(raw).toUpperCase();
  const label = formatEnumLabel(code);
  if (isApprovedKycStatus(code)) {
    return {label, color: 'green'};
  }
  if (['PENDING', 'SUBMITTED', 'IN_REVIEW', 'IN_PROGRESS'].includes(code)) {
    return {label, color: 'gold'};
  }
  if (
    ['REJECTED', 'DECLINED', 'FAILED', 'NOT_APPROVED', 'UNVERIFIED'].includes(
      code,
    )
  ) {
    return {label, color: 'red'};
  }
  return {label, color: 'default'};
};

const accreditedDisplay = raw => {
  if (raw == null) return {show: false};
  if (typeof raw === 'boolean') {
    return {show: true, yes: raw};
  }
  const s = String(raw).toLowerCase();
  if (s === 'true' || s === '1') return {show: true, yes: true};
  if (s === 'false' || s === '0') return {show: true, yes: false};
  return {show: true, yes: Boolean(raw)};
};

/**
 * @param {{ purposeOptions: { value: string, label: string }[] }} props
 */
export const InvestmentPurposeUsersTable = ({purposeOptions = []}) => {
  const [selectedPurpose, setSelectedPurpose] = useState(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportForm] = Form.useForm();
  const loggedInEmail = useSelector(
    state => state?.auth?.userData?.user?.email ?? '',
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedPurpose]);

  useEffect(() => {
    if (
      selectedPurpose != null &&
      purposeOptions.length > 0 &&
      !purposeOptions.some(o => o.value === selectedPurpose)
    ) {
      setSelectedPurpose(undefined);
    }
  }, [purposeOptions, selectedPurpose]);

  const queryFilters = useMemo(() => {
    const base = {
      page: currentPage,
      limit,
      paginate: true,
    };
    if (selectedPurpose) {
      base.investmentPurpose = selectedPurpose;
    }
    return base;
  }, [currentPage, limit, selectedPurpose]);

  const {data, loading} = useQuery(GET_INVESTMENT_PURPOSE_USERS, {
    variables: {filters: queryFilters},
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    onError: error => toast.error(error.message),
  });

  const [exportUsers, {loading: exportLoading}] = useLazyQuery(
    GET_INVESTMENT_PURPOSE_USERS,
    {
      fetchPolicy: 'no-cache',
      onError: error => toast.error(error.message),
    },
  );

  const node = data?.getInvestmentPurposeUsers;
  const isError = node?.__typename === 'Error';
  const rows = !isError && node?.data ? node.data : [];
  const total = !isError && node?.pagination ? node.pagination.total || 0 : 0;

  useEffect(() => {
    if (isError && node?.message) {
      toast.error(node.message);
    }
  }, [isError, node]);

  const columns = [
    {
      title: 'User',
      key: 'user',
      render: (_, record) => {
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}>
            <Avatar icon={<UserOutlined />} />
            <div style={{display: 'flex', flexDirection: 'column'}}>
              <span style={{fontWeight: 500, color: '#111827'}}>
                <UserLink userId={record?.id}>
                  {[record?.firstName, record?.lastName]
                    .filter(Boolean)
                    .join(' ') || '—'}
                </UserLink>
              </span>
              <span style={{fontSize: '12px', color: '#6b7280'}}>
                {record?.email || '—'}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      title: 'User type',
      dataIndex: 'userType',
      key: 'userType',
      render: text =>
        text ? <Tag color="blue">{formatEnumLabel(text)}</Tag> : '—',
    },
    {
      title: 'Joined',
      key: 'createdAt',
      render: (_, record) => {
        const ts = record?.createdAt;
        if (ts == null || ts === '') return '—';
        const m = moment(ts);
        return m.isValid() ? m.format('DD MMMM YYYY') : '—';
      },
    },
    {
      title: 'Sign-up medium',
      key: 'signupMedium',
      render: (_, record) => {
        const medium = record?.currentSessionDetails?.medium;
        if (medium == null || medium === '') return '—';
        return formatEnumLabel(String(medium));
      },
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
      render: text =>
        text == null || text === ''
          ? '—'
          : formatEnumLabel(String(text).replace(/_/g, ' ')),
    },
    {
      title: 'Investment purposes',
      key: 'investmentPurposes',
      render: (_, record) => {
        const raw = record?.investorProfile?.riskNeed?.investmentPurposes;
        const keys = getActiveInvestmentPurposeKeys(raw);
        if (!keys.length) return '—';
        return (
          <span style={{display: 'flex', flexWrap: 'wrap', gap: 6}}>
            {keys.map((key, i) => (
              <Tag key={`${key}-${i}`}>{formatEnumLabel(key)}</Tag>
            ))}
          </span>
        );
      },
    },
    {
      title: 'KYC status',
      key: 'isKycApproved',
      render: (_, record) => {
        const v = record?.investorProfile?.isKycApproved;
        const {label, color} = kycStatusDisplay(v);
        if (label === '—') return '—';
        return <Tag color={color}>{label}</Tag>;
      },
    },
    {
      title: 'Accredited',
      key: 'isAccredited',
      render: (_, record) => {
        const acc = accreditedDisplay(record?.investorProfile?.isAccredited);
        if (!acc.show) return '—';
        return (
          <Tag color={acc.yes ? 'green' : 'default'}>
            {acc.yes ? 'Yes' : 'No'}
          </Tag>
        );
      },
    },
    {
      title: 'Segments',
      key: 'userSegments',
      render: (_, record) => {
        const segments = Array.isArray(record?.userSegments)
          ? record.userSegments
          : [];
        const names = segments
          .map(s => s?.name)
          .filter(n => n != null && n !== '');
        if (!names.length) return '—';
        return (
          <span style={{display: 'flex', flexWrap: 'wrap', gap: 6}}>
            {names.map((name, i) => (
              <Tag key={`${name}-${i}`} color="purple">
                {formatEnumLabel(name)}
              </Tag>
            ))}
          </span>
        );
      },
    },
  ];

  const handleTableChange = pagination => {
    setCurrentPage(pagination.current);
    setLimit(pagination.pageSize);
  };

  const handleExportModalOpen = () => {
    exportForm.resetFields();
    setExportModalOpen(true);
  };

  const handleExportFinish = values => {
    if (!selectedPurpose) {
      toast.error('Select an investment purpose before exporting.');
      return;
    }

    const typed = (values?.email ?? '').trim();
    const email = typed || String(loggedInEmail || '').trim();

    if (!email) {
      toast.error(
        'Could not determine an export email. Please enter your Daba email.',
      );
      return;
    }

    if (!email.endsWith('@joindaba.com')) {
      toast.error('Only daba emails are allowed');
      return;
    }

    const filters = {
      investmentPurpose: selectedPurpose,
      export: true,
      email,
    };

    exportUsers({variables: {filters}})
      .then(({data: exportData}) => {
        const res = exportData?.getInvestmentPurposeUsers;
        if (res?.__typename === 'Error') {
          toast.error(res.message);
          return;
        }
        toast.success(
          res?.message ||
            'Export started. Please check your email (including spam).',
        );
        setExportModalOpen(false);
        exportForm.resetFields();
      })
      .catch(() => toast.error('Export failed'));
  };

  return (
    <div className={styles.membersTableContainer}>
      <div className={styles.membersHeader}>
        <h3 className={styles.sectionTitle}>
          Users (investment purpose responses)
        </h3>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-end',
            gap: 12,
          }}>
          <div>
            <div
              style={{
                fontSize: 12,
                color: '#6b7280',
                marginBottom: 4,
                fontWeight: 500,
              }}>
              Investment purpose
            </div>
            <Select
              style={{minWidth: 280}}
              placeholder="Select purpose"
              options={purposeOptions}
              value={selectedPurpose}
              onChange={value =>
                setSelectedPurpose(value === undefined ? undefined : value)
              }
              allowClear
              showSearch
              optionFilterProp="label"
              disabled={!purposeOptions.length}
            />
          </div>
          <SubmitButton
            type="button"
            label="Export"
            handleClick={handleExportModalOpen}
            disabled={!selectedPurpose}
            smallWidth
          />
        </div>
      </div>
      <Table
        columns={columns}
        dataSource={rows}
        loading={loading}
        rowKey={(row, index) => row?.id ?? `${row?.email ?? 'row'}-${index}`}
        pagination={{
          current: currentPage,
          pageSize: limit,
          total,
          showSizeChanger: true,
          position: ['bottomRight'],
        }}
        onChange={handleTableChange}
        className={styles.antdTable}
      />

      <DBModal
        isOpen={exportModalOpen}
        handleClose={() => {
          setExportModalOpen(false);
          exportForm.resetFields();
        }}
        content={
          <div style={{maxWidth: 440}}>
            <h2 style={{marginTop: 0}}>Export users</h2>
            <p style={{color: '#595959', marginBottom: 16}}>
              A CSV will be sent to the Daba email below. The mail might be in
              your spam folder.
            </p>
            <Form
              form={exportForm}
              layout="vertical"
              onFinish={handleExportFinish}>
              <Form.Item
                name="email"
                label={
                  <span>
                    <Tooltip title="If left empty, your logged-in admin email is used. Must be a @joindaba.com address.">
                      Export email (optional)
                    </Tooltip>
                  </span>
                }
                rules={[
                  {
                    validator: async (_, value) => {
                      const v = value != null ? String(value).trim() : '';
                      if (!v) {
                        return;
                      }
                      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
                        throw new Error('Please enter a valid email address');
                      }
                      if (!v.endsWith('@joindaba.com')) {
                        throw new Error('Only daba emails are allowed');
                      }
                    },
                  },
                ]}>
                <Input
                  type="email"
                  placeholder={loggedInEmail || 'you@joindaba.com'}
                  allowClear
                />
              </Form.Item>
              <SubmitButton
                label={exportLoading ? 'Sending…' : 'Send export'}
                disabled={exportLoading}
              />
            </Form>
          </div>
        }
      />
    </div>
  );
};
